/**
 * @typedef {Object} ImportFieldDisplayLabelContext
 * @property {Array<{ name: string, title: string }>} [mappingFields]
 * @property {Array<{ id: number, levelIndex?: number }>} [allPriceLevels]
 * @property {(title: string, levelIndex: number) => string} [formatPriceLevelLabel]
 */

/**
 * Returns a user-facing label for an import field, appending a disambiguator when
 * multiple mapped fields share the same display title (e.g. duplicate price level names).
 *
 * @param {{ name: string, title: string }|undefined|null} field
 * @param {ImportFieldDisplayLabelContext} [context]
 * @returns {string}
 */
export function getImportFieldDisplayLabel(field, context = {}) {
    if (!field) {
        return '';
    }

    const {mappingFields = [], allPriceLevels = [], formatPriceLevelLabel} = context;

    const duplicateTitleCount = mappingFields.filter((f) => f.title === field.title).length;
    if (duplicateTitleCount <= 1) {
        return field.title;
    }

    const priceLevelMatch = field.name.match(/^priceLevel_(\d+)$/);
    if (priceLevelMatch) {
        const levelId = Number(priceLevelMatch[1]);
        const level = allPriceLevels.find((l) => l.id === levelId);
        const levelIndex = level?.levelIndex ?? levelId;
        if (formatPriceLevelLabel) {
            return formatPriceLevelLabel(field.title, levelIndex);
        }
        return `${field.title} (Price Level ${levelIndex})`;
    }

    return `${field.title} (${field.name})`;
}
